import React from 'react';
import { Flex, Switch } from 'antd';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { trackEvent } from 'jf/analytics/Analytics';
import { StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExText } from 'jf/components/DevExText';
import { useTargetingClient } from 'jf/services/useTargetingClient';
import { queryClient } from 'jf/utils/queryClient';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

import { SendStudyReminderModal } from '../SendStudyReminderModal';

export const ManageRemindersCard: React.FC<{ studyRef: string; onClose: () => void }> = (props) => {
  const theme = useDevExTheme();
  const [isEditing, setIsEditing] = React.useState(false);

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef: props.studyRef });
  const { mutateAsync: updateStudy, isLoading: isUpdating } = useClientMutation(
    StudyClient.updateStudy
  );
  const enableScheduledReminders = study?.enableScheduledReminders;
  const sendStudyReminderModal = useJFOverlay(SendStudyReminderModal);

  const [newEnableScheduledReminders, setNewEnableScheduledReminders] =
    React.useState(enableScheduledReminders);

  const { msgProvider, msgIcon } = useTargetingClient();

  const onSendReminder = () => {
    trackEvent(`survey-analyzer:send-slack-reminder:open`);
    props.onClose();
    sendStudyReminderModal.open();
  };
  const onSave = () => {
    updateStudy({
      studyRef: props.studyRef,
      requestBody: {
        enableScheduledReminders: newEnableScheduledReminders,
      },
    }).then(() => {
      queryClient.invalidateQueries(['GET_STUDY', props.studyRef]);
      queryClient.invalidateQueries('GET_STUDIES');
    });
    setIsEditing(false);
  };
  return (
    <DevExCard
      title={
        <DevExText fontSize="md" strong>
          Survey Reminders
        </DevExText>
      }
      extra={
        <Flex gap={theme.variable.spacing.sm}>
          {isEditing ? (
            <>
              <DevExButton
                onClick={() => {
                  setIsEditing(false);
                  setNewEnableScheduledReminders(enableScheduledReminders);
                }}
              >
                Cancel
              </DevExButton>
              <DevExButton type="primary" disabled={isUpdating} onClick={onSave}>
                Save
              </DevExButton>
            </>
          ) : (
            <>
              <DevExButton
                icon={<FontAwesomeIcon icon={msgIcon} />}
                type="secondary"
                disabled={enableScheduledReminders}
                onClick={onSendReminder}
              >
                Send now
              </DevExButton>
              <DevExButton
                icon={<FontAwesomeIcon icon={faPen} />}
                onClick={() => setIsEditing(true)}
                type="outline"
              />
            </>
          )}
        </Flex>
      }
    >
      {isEditing ? (
        <Flex align="center" gap={theme.variable.spacing.md}>
          <Switch
            value={newEnableScheduledReminders}
            size="small"
            onChange={(checked) => {
              setNewEnableScheduledReminders(checked);
            }}
          />
          <DevExText lined>
            Send automatic reminders every <DevExText strong>2 days </DevExText> via {msgProvider}.
          </DevExText>
        </Flex>
      ) : enableScheduledReminders ? (
        <DevExText lined>
          Reminders are sent automatically every <DevExText strong>2 days </DevExText> via{' '}
          {msgProvider} to respondents who have not yet responded.
        </DevExText>
      ) : (
        <DevExText lined>
          Automatic reminders are not enabled for this survey. Send a reminder in {msgProvider} to
          survey recipients that have not yet responded.
        </DevExText>
      )}
    </DevExCard>
  );
};
